/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * P-5 (S629) — the shop's supported languages, declared once.
 *
 * This is the whitelist every other piece of the language chain asks. The
 * router guard, the switcher, the storage reader, the checkout request and the
 * i18next loader all read it, so there is one answer to "is this a language
 * this shop speaks" rather than four that can disagree.
 *
 * N-12: before this existed, `/:lang` matched any value. `/zz/cart` rendered in
 * English while every link on the page kept the `/zz/` prefix, and `zz` was sent
 * to the server as the order's language. Nothing broke, because the server
 * whitelists the value before putting it in a link — but the customer stayed in
 * a language that does not exist.
 *
 * Stripe's hosted payment page supports all six of these (en, de, fr, es, it,
 * pt are all on its published Checkout list), which is why setting the language
 * on the session is enough and Stripe's own field labels are out of scope.
 *
 * `pt` is Brazilian Portuguese. N-5 (S703) corrects the second half of what
 * this note used to say — "the shop's, the Deskshare website's and Stripe's
 * `pt` all mean the same variety, so there is no locale mapping here". Stripe
 * lists `pt` (Portugal) and `pt-BR` (Brazil) as DIFFERENT locales, on Checkout
 * and on Invoicing alike, so a bare `pt` asked every Stripe-hosted page and
 * every Stripe field for European Portuguese. `toStripeLocale()` below is the
 * mapping that was said not to be needed.
 */

export const SUPPORTED_LANGUAGES = ["en", "de", "fr", "es", "pt", "it"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** English is the fallback everywhere: it is the statically bundled language. */
export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

/** Human names for the switcher, each written in its own language. */
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  pt: "Português",
  it: "Italiano",
};

/**
 * The BCP-47 tag to use when formatting numbers, currency and dates for a
 * language. N-10: the shop had four different fixed answers to this question
 * ("en-US" twice, "en-GB" once, and the browser's own language everywhere
 * else). One table means one answer.
 *
 * `pt-BR` and not bare `pt`, because the variety is decided and the grouping
 * and date order differ between the two.
 */
export const LANGUAGE_LOCALES: Record<SupportedLanguage, string> = {
  en: "en-US",
  de: "de-DE",
  fr: "fr-FR",
  es: "es-ES",
  pt: "pt-BR",
  it: "it-IT",
};

/**
 * The tag to use for DATES, which is not always the same as the one for money.
 *
 * English is the exception and it is deliberate: this shop has always written
 * dates as "27 Aug 2026", and that is what the Billing & Payments column widths
 * in P-1 and P-2 are measured against. Switching English to "Aug 27, 2026"
 * would be a visible change to every existing customer's dashboard that nobody
 * asked for, made as a side effect of a localization change — so English keeps
 * en-GB for dates while keeping en-US for currency, exactly as it reads today.
 *
 * Every other language gets its own convention for both, which is the whole
 * point of N-10.
 */
export const LANGUAGE_DATE_LOCALES: Record<SupportedLanguage, string> = {
  en: "en-GB",
  de: "de-DE",
  fr: "fr-FR",
  es: "es-ES",
  pt: "pt-BR",
  it: "it-IT",
};

/** True when `value` is a language this shop actually speaks. */
export function isSupportedLang(value: unknown): value is SupportedLanguage {
  return (
    typeof value === "string" &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(value.toLowerCase())
  );
}

/** The supported language `value` names, or the default when it names none. */
export function toSupportedLang(value: unknown): SupportedLanguage {
  return isSupportedLang(value)
    ? (value.toLowerCase() as SupportedLanguage)
    : DEFAULT_LANGUAGE;
}

/** The Intl locale tag for a language, for numbers and currency. */
export function localeFor(lang: unknown): string {
  return LANGUAGE_LOCALES[toSupportedLang(lang)];
}

/** The Intl locale tag for a language, for dates. See LANGUAGE_DATE_LOCALES. */
export function dateLocaleFor(lang: unknown): string {
  return LANGUAGE_DATE_LOCALES[toSupportedLang(lang)];
}

/**
 * The Stripe locale for a shop language. N-5 (S703).
 *
 * Used everywhere a language is handed to Stripe from the browser: both
 * `<Elements>` providers, through `stripe-elements-provider.tsx` (A-3). The
 * server half of the same mapping is `stripe_locale()` in
 * `backend/apps/core/shop_language.py`, and a test asserts the two agree — a
 * mapping that exists on one side only is how `pt` came to mean two things.
 *
 * Every other shop language is spelled the same on both sides, so the table is
 * one entry rather than six.
 */
export type StripeShopLocale = Exclude<SupportedLanguage, "pt"> | "pt-BR";

export function toStripeLocale(value: unknown): StripeShopLocale {
  const lang = toSupportedLang(value);
  return lang === "pt" ? "pt-BR" : lang;
}
